import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toStorageUrl } from '../../utils/storageUrl';
import { extractApiList } from '../../utils/extractApiList';
import { useInView } from '../../hooks/useInView';

/* ─── SVG Icons ─── */
const ArrowRightIcon = () => (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="10" x2="17" y2="10"/>
    <polyline points="12,5 17,10 12,15"/>
  </svg>
);
const RocketIcon = () => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2C10 2 14 5 14 11L10 15 6 11C6 5 10 2 10 2z" fill="rgba(255,255,255,0.15)"/>
    <circle cx="10" cy="8" r="1.8"/>
    <path d="M6 11L3 16 7 14.5M14 11L17 16 13 14.5"/>
  </svg>
);
const MailIcon = () => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="16" height="12" rx="2"/>
    <polyline points="2,4 10,11 18,4"/>
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="4" y1="4" x2="16" y2="16"/><line x1="16" y1="4" x2="4" y2="16"/>
  </svg>
);
const ZoomInIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="9" r="6"/><line x1="13.5" y1="13.5" x2="18" y2="18"/>
    <line x1="9" y1="6" x2="9" y2="12"/><line x1="6" y1="9" x2="12" y2="9"/>
  </svg>
);
const ZoomOutIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="9" r="6"/><line x1="13.5" y1="13.5" x2="18" y2="18"/>
    <line x1="6" y1="9" x2="12" y2="9"/>
  </svg>
);
const DownloadIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 14v3h14v-3M10 3v9M6 8l4 4 4-4"/>
  </svg>
);
const WarningIcon = () => (
  <svg viewBox="0 0 48 48" width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 6L4 42h40L24 6z" stroke="#ff4444" strokeWidth="2" fill="rgba(255,68,68,0.1)" strokeLinejoin="round"/>
    <line x1="24" y1="20" x2="24" y2="30" stroke="#ff4444" strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="24" cy="36" r="1.5" fill="#ff4444"/>
  </svg>
);
const RetryIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 4v6h6M23 20v-6h-6"/>
    <path d="M20.49 9A9 9 0 005.64 5.64L1 10m18 0l-4.64 4.36A9 9 0 013.51 11"/>
  </svg>
);
const FilterIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
  </svg>
);

/* ─── Particle Canvas ─── */
const ParticleCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    let raf;
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    resize(); window.addEventListener('resize', resize);
    const pts = Array.from({ length: 48 }, () => ({
      x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.3 + 0.4, a: Math.random() * 0.4 + 0.07,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = c.width; if (p.x > c.width) p.x = 0;
        if (p.y < 0) p.y = c.height; if (p.y > c.height) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,68,68,${p.a})`; ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y, d = Math.sqrt(dx*dx+dy*dy);
        if (d < 115) {
          ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y);
          ctx.strokeStyle = `rgba(255,68,68,${0.06*(1-d/115)})`; ctx.lineWidth=0.5; ctx.stroke();
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} style={{position:'fixed',inset:0,zIndex:0,pointerEvents:'none'}} />;
};

/* ─── Animated Counter ─── */
const AnimCounter = ({ target, inView }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let step = 0; const steps = 55;
    const t = setInterval(() => {
      step++; setVal(Math.round(target * step / steps));
      if (step >= steps) clearInterval(t);
    }, 1400 / steps);
    return () => clearInterval(t);
  }, [inView, target]);
  return <>{val}</>;
};

/* ─── Section Header ─── */
const SectionHeader = ({ tag, title, subtitle, inView }) => (
  <div className="sh">
    <span className="sh-tag">{tag}</span>
    <h2 className={`sh-title${inView ? ' ul-in' : ''}`}>{title}</h2>
    <div className="sh-bar"/>
    {subtitle && <p className="sh-sub">{subtitle}</p>}
  </div>
);

/* ─── Activity Card ─── */
const ActivityCard = ({ activity, index, onPreview }) => {
  const [ref, inView] = useInView(0.08);
  return (
    <div ref={ref} className={`ac ${inView ? 'ac-in' : ''}`} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
      <div className="ac-top-line"/>

      {/* Header row */}
      <div className="ac-header">
        <div className="ac-head-right">
          <button className="ac-head-btn" onClick={() => onPreview(activity.representative_picture)}>
            DETAILS <ArrowRightIcon/>
          </button>
        </div>
      </div>

      {/* Image */}
      <div className="ac-img-wrap" onClick={() => onPreview(activity.representative_picture)}>
        <img src={toStorageUrl(activity.representative_picture)} alt={activity.displayName} className="ac-img" />
        <div className="ac-img-overlay">
          <div className="ac-view-badge">
            <ZoomInIcon/><span>VIEW</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="ac-body">
        {activity.tags.length > 0 && (
          <div className="ac-tags">
            {activity.tags.map((t, i) => <span key={i} className="ac-tag">{t}</span>)}
          </div>
        )}
        <h3 className="ac-title">{activity.displayName}</h3>
        {activity.achievement_description && (
          <p className="ac-desc">{activity.achievement_description}</p>
        )}
      </div>
      <div className="ac-bottom-line"/>
    </div>
  );
};

const ActivityCardSkeleton = ({ index }) => (
  <div className="ac ac-skeleton" style={{ animationDelay: `${(index % 3) * 0.08}s` }}>
    <div className="ac-top-line" />
    <div className="ac-header">
      <div className="ac-head-right">
        <span className="sk sk-btn" />
      </div>
    </div>
    <div className="ac-img-wrap">
      <div className="sk sk-img" />
    </div>
    <div className="ac-body">
      <div className="ac-tags">
        <span className="sk sk-tag" />
        <span className="sk sk-tag" />
      </div>
      <div className="sk sk-title" />
      <div className="sk sk-desc" />
      <div className="sk sk-desc sk-desc-short" />
    </div>
    <div className="ac-bottom-line" />
  </div>
);

/* ─── Image Preview ─── */
const ImagePreview = ({ image, onClose, onDownload }) => {
  const [zoom, setZoom] = useState(1);
  useEffect(() => { setZoom(1); }, [image]);
  if (!image) return null;
  return (
    <div className="preview-overlay" onClick={onClose}>
      <div className="preview-panel" onClick={e => e.stopPropagation()}>
        <div className="preview-top-line"/>
        <div className="preview-controls">
          <button className="prev-ctrl" onClick={() => setZoom(z => Math.min(z + 0.15, 3))} title="Zoom In"><ZoomInIcon/></button>
          <button className="prev-ctrl" onClick={() => setZoom(z => Math.max(z - 0.15, 0.5))} title="Zoom Out"><ZoomOutIcon/></button>
          <button className="prev-ctrl" onClick={() => onDownload(image)} title="Download"><DownloadIcon/></button>
          <button className="prev-ctrl prev-close" onClick={onClose} title="Close"><CloseIcon/></button>
        </div>
        <div className="preview-img-area">
          <img
            src={toStorageUrl(image)}
            alt="Preview"
            className="preview-img"
            style={{ transform: `scale(${zoom})`, transition: 'transform 0.25s ease' }}
          />
        </div>
        <div className="preview-zoom-tag">// ZOOM: {Math.round(zoom * 100)}%</div>
      </div>
    </div>
  );
};

/* ─── Main Component ─── */
const Activity = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedYear, setSelectedYear] = useState('all');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  /** Populated after each successful fetch; helps verify API vs client filters when the grid is empty */
  const [lastFetchStats, setLastFetchStats] = useState(null);

  const [actsRef, actsInView] = useInView();
  const [statsRef, statsInView] = useInView();
  const [upcomingRef, upcomingInView] = useInView();

  useEffect(() => { setTimeout(() => setIsVisible(true), 120); fetchActivities(); }, []);

  const getDisplayName = (a) => a.event_name || a.event?.name || 'Untitled Activity';
  const getTags = (name) => {
    const tags = []; if (!name) return tags;
    const n = name.toLowerCase();
    if (n.includes('national')) tags.push('National');
    if (n.includes('international')) tags.push('International');
    if (n.includes('workshop')) tags.push('Workshop');
    if (n.includes('competition')) tags.push('Competition');
    if (n.includes('team')) tags.push('Team Building');
    if (n.includes('outreach')) tags.push('Outreach');
    if (n.includes('education')) tags.push('Education');
    if (n.includes('community')) tags.push('Community');
    return tags;
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/public/activities', {
        cache: false,
        params: { _t: Date.now() },
      });
      const rows = extractApiList(response.data);
      setLastFetchStats({
        apiRowCount: rows.length,
        publicCount: rows.length,
        activityEligibleCount: rows.length,
      });
      const publicActivities = rows;
      const mapped = publicActivities.map(item => {
        const name = getDisplayName(item);
        return { ...item, displayName: name, year: new Date(item.created_at).getFullYear().toString(), tags: getTags(name) };
      });
      setActivities(mapped);
    } catch (err) {
      setLastFetchStats(null);
      if (err.response) setError(`Server error ${err.response.status} — please try again later.`);
      else if (err.request) setError('Unable to connect. Check your internet connection.');
      else setError('Failed to load activities. Please try again.');
    } finally { setLoading(false); }
  };

  const handleDownload = async (imagePath) => {
    try {
      const response = await api.get(`/galleries/download/${imagePath}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', imagePath.split('/').pop());
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { console.error('Download error:', err); }
  };

  const activityYears = [...new Set(activities.map((a) => a.year))].sort((a, b) => b - a);
  const filtered = selectedYear === 'all' ? activities : activities.filter(a => a.year === selectedYear);
  const foundingYear = activities[activities.length - 1]?.year || '2002';

  if (loading) return (
    <div className="pg ext-page">
      <ParticleCanvas />
      <div className="grid-bg" /><div className="scanlines" />
      <div className="state-wrap">
        <div className="loading-rig">
          <div className="lr-ring lr-ring-1" />
          <div className="lr-ring lr-ring-2" />
          <div className="lr-ring lr-ring-3" />
          <div className="lr-core" />
        </div>
        <div className="load-tag">// FETCHING_ACTIVITIES</div>
        <h2 className="load-title">LOADING ACTIVITIES</h2>
        <p className="load-sub">Please wait while we retrieve activity data…</p>
      </div>
      <Styles />
    </div>
  );

  if (error) return (
    <div className="pg ext-page">
      <ParticleCanvas />
      <div className="grid-bg" /><div className="scanlines" />
      <div className="state-wrap">
        <div className="err-icon"><WarningIcon /></div>
        <div className="err-tag">// ERROR_STATE</div>
        <h2 className="err-title">CONNECTION FAILED</h2>
        <p className="err-msg">{error}</p>
        <button type="button" className="retry-btn" onClick={fetchActivities}>
          <span className="retry-shine" />
          <span className="retry-inner"><RetryIcon /> RETRY CONNECTION</span>
        </button>
      </div>
      <Styles />
    </div>
  );

  return (
    <div className="pg ext-page">
      <ParticleCanvas/>
      <div className="grid-bg" aria-hidden="true"/>
      <div className="scanlines" aria-hidden="true"/>
      <div className="radial-glow" aria-hidden="true"/>

      <div className="wrapper">

        {/* ── HERO ── */}
        <section className={`hero ${isVisible ? 'vis' : ''}`}>
          <div className="hero-tag">
            <span className="ht-dot"/>
            <span>UNIVERSITI TEKNOLOGI MALAYSIA // ACTIVITIES_MODULE</span>
          </div>
          <h1 className="hero-title">
            OUR<br/>
            <span className="hero-accent">ACTI<span className="hero-red">VITIES</span></span>
          </h1>
          <div className="hero-divider">
            <div className="hd-l"/><span className="hd-text">EVENTS_LOG</span><div className="hd-r"/>
          </div>
          <p className="hero-desc">
            Celebrating over two decades of excellence, innovation, and achievements in robotics competitions,
            workshops, and community outreach since <em>{foundingYear}</em>.
          </p>
          <div className="hero-sweep-line"><div className="hsl-inner"/></div>
        </section>

        {/* ── FILTER ── */}
        <section className="filter-sec">
          <div className="filter-bar">
            <div className="filter-group">
              <FilterIcon />
              <span className="filter-label">YEAR</span>
              <select
                className="filter-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="all">ALL YEARS</option>
                {activityYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* ── ACTIVITIES ── */}
        <section className={`sec ${actsInView ? 'sec-in' : ''}`} ref={actsRef}>
          <div className="sec-shell">
            <SectionHeader
              tag="// ACTIVITY_LOG"
              title="OUR JOURNEY"
              subtitle={`From our founding in ${foundingYear} to our latest achievements — explore every milestone`}
              inView={actsInView}
            />
            {filtered.length === 0 ? (
              <div className="glass-card empty-card">
                <div className="card-top-line"/>
                <div className="empty-tag">// NO_RECORDS_FOUND</div>
                <h3 className="empty-title">NO ACTIVITIES FOR {selectedYear === 'all' ? 'THIS PERIOD' : selectedYear}</h3>
                <p className="empty-desc">Try selecting a different year or check back later.</p>
                {lastFetchStats && (
                  <p className="debug-fetch-hint" role="status">
                    Debug: API returned {lastFetchStats.apiRowCount} row(s). Public rows: {lastFetchStats.publicCount}.
                    After public + non-achievement filter: {lastFetchStats.activityEligibleCount}.
                    {selectedYear !== 'all' && activities.length > 0 && (
                      <> Year filter hides all listed items ({activities.length} in other years).</>
                    )}
                  </p>
                )}
                <div className="card-bottom-line"/>
              </div>
            ) : (
              <div className="acts-grid">
                {filtered.map((a, i) => (
                  <ActivityCard key={i} activity={a} index={i} onPreview={setPreviewImage}/>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── UPCOMING ── */}
        <section className={`sec ${upcomingInView ? 'sec-in' : ''}`} ref={upcomingRef}>
          <div className="sec-shell">
            <div className="glass-card upcoming-card">
              <div className="uc-sweep"/>
              <div className="card-top-line"/>
              <div className="uc-alert"><span className="uca-dot"/><span>// UPCOMING_EVENTS</span></div>
              <h2 className="uc-title">WHAT'S NEXT?</h2>
              <p className="uc-desc">
                Stay tuned for upcoming events and activities — workshops, competitions, and team building sessions are regularly organized.
              </p>
              <div className="uc-btns">
                <button className="btn-primary">
                  <span className="btn-shine"/>
                  <span className="btn-inner"><RocketIcon/> JOIN OUR TEAM</span>
                </button>
                <button className="btn-secondary"><MailIcon/> GET UPDATES</button>
              </div>
              <div className="card-bottom-line"/>
            </div>
          </div>
        </section>

      </div>

      {/* ── PREVIEW ── */}
      <ImagePreview image={previewImage} onClose={() => setPreviewImage(null)} onDownload={handleDownload}/>

      <Styles/>
    </div>
  );
};

/* ─── Styles ─── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700;900&family=Rajdhani:wght@300;400;500;600;700&family=Share+Tech+Mono&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

    .pg{position:relative;min-height:100vh;background:#060608;font-family:'Rajdhani',-apple-system,BlinkMacSystemFont,sans-serif;color:#e0e0e0;overflow-x:hidden;}

    /* Overlays */
    .grid-bg{position:fixed;inset:0;z-index:1;pointer-events:none;background-image:linear-gradient(rgba(255,68,68,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,68,68,0.03) 1px,transparent 1px);background-size:72px 72px;}
    .scanlines{position:fixed;inset:0;z-index:2;pointer-events:none;background:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.05) 2px,rgba(0,0,0,0.05) 4px);}
    .radial-glow{position:fixed;inset:0;z-index:1;pointer-events:none;background:radial-gradient(ellipse at 20% 80%,rgba(255,34,34,0.06),transparent 55%),radial-gradient(ellipse at 80% 20%,rgba(255,34,34,0.04),transparent 55%);}

    /* Shared wrapper/section/header styles are in external.css */

    /* ── Hero ── */
    .hero{padding:6.5rem 0 4.5rem;text-align:center;opacity:0;transform:translateY(26px);transition:opacity 0.9s ease,transform 0.9s ease;}
    .hero.vis{opacity:1;transform:none;}
    .hero-tag{display:inline-flex;align-items:center;gap:8px;background:rgba(255,68,68,0.07);border:1px solid rgba(255,68,68,0.2);border-radius:2px;padding:5px 14px;margin-bottom:1.8rem;font-family:'Share Tech Mono',monospace;font-size:0.62rem;color:#ff7766;letter-spacing:1.5px;}
    .ht-dot{width:7px;height:7px;border-radius:50%;background:#ff4444;box-shadow:0 0 8px #ff4444;animation:blink 1.6s ease-in-out infinite;}
    @keyframes blink{0%,100%{opacity:1;box-shadow:0 0 8px #ff4444}50%{opacity:0.3;box-shadow:0 0 3px #ff4444}}
    .hero-title{font-family:'Orbitron',sans-serif;font-size:clamp(2.8rem,7vw,5.5rem);font-weight:900;color:#fff;line-height:0.9;margin-bottom:1.6rem;}
    .hero-accent{display:block;}
    .hero-red{color:#ff2222;text-shadow:0 0 18px rgba(255,34,34,0.7),0 0 40px rgba(255,34,34,0.35);animation:glow-pulse 3s ease-in-out infinite;}
    @keyframes glow-pulse{0%,100%{text-shadow:0 0 18px rgba(255,34,34,0.7),0 0 40px rgba(255,34,34,0.35)}50%{text-shadow:0 0 28px rgba(255,34,34,0.95),0 0 60px rgba(255,34,34,0.55)}}
    .hero-divider{display:flex;align-items:center;gap:1rem;margin:0 auto 1.5rem;max-width:460px;}
    .hd-l{flex:1;height:1px;background:linear-gradient(90deg,transparent,#ff4444);}
    .hd-r{flex:1;height:1px;background:linear-gradient(90deg,#ff4444,transparent);}
    .hd-text{font-family:'Share Tech Mono',monospace;font-size:0.6rem;color:#ff6644;letter-spacing:3px;white-space:nowrap;}
    .hero-desc{font-size:1.1rem;color:#8a97a8;line-height:1.75;max-width:620px;margin:0 auto 1.6rem;}
    .hero-desc em{color:#ff8877;font-style:normal;font-weight:600;}
    .hero-sweep-line{width:120px;height:2px;background:rgba(255,68,68,0.18);border-radius:1px;overflow:hidden;position:relative;margin:0 auto;}
    .hsl-inner{position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,#ff4444,transparent);animation:sweep-ln 2.5s ease-in-out infinite;}
    @keyframes sweep-ln{0%{left:-100%}100%{left:200%}}

    /* ── Glass card ── */
    .glass-card{background:rgba(8,8,14,0.86);border:1px solid rgba(255,68,68,0.12);border-radius:5px;position:relative;overflow:hidden;backdrop-filter:blur(18px);}
    .card-top-line{position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,68,68,0.65),transparent);pointer-events:none;}
    .card-bottom-line{position:absolute;bottom:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,68,68,0.2),transparent);pointer-events:none;}

    /* ── Activities grid ── */
    .acts-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(360px,1fr));gap:1.8rem;}
    .sec-loading-tag{font-family:'Share Tech Mono',monospace;font-size:0.62rem;color:#ff6644;letter-spacing:1.8px;margin-bottom:1.2rem;text-align:center;}

    /* ── Activity card ── */
    .ac{background:rgba(8,8,14,0.86);border:1px solid rgba(255,68,68,0.1);border-radius:5px;position:relative;overflow:hidden;backdrop-filter:blur(18px);transition:border-color 0.3s,box-shadow 0.3s,transform 0.3s;opacity:1;}
    .ac.ac-in{animation:card-up 0.55s ease both;}
    @keyframes card-up{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
    .ac:hover{border-color:rgba(255,68,68,0.35);transform:translateY(-5px);box-shadow:0 10px 36px rgba(255,68,68,0.12);}
    .ac-top-line{position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,68,68,0.55),transparent);}
    .ac-bottom-line{position:absolute;bottom:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,68,68,0.18),transparent);}
    .ac-skeleton{opacity:1;animation:card-up 0.45s ease both;}

    .ac-header{display:flex;justify-content:space-between;align-items:center;padding:1.2rem 1.4rem 0;gap:0.8rem;}
    .ac-head-right{display:flex;align-items:center;gap:0.55rem;}
    .ac-cat-badge{font-family:'Share Tech Mono',monospace;font-size:0.58rem;font-weight:700;letter-spacing:1.5px;padding:3px 10px;border-radius:2px;text-transform:uppercase;}
    .ac-year{font-family:'Orbitron',sans-serif;font-size:0.7rem;font-weight:700;color:#ff4444;background:rgba(255,68,68,0.1);border:1px solid rgba(255,68,68,0.25);border-radius:2px;padding:3px 10px;letter-spacing:1px;}
    .ac-head-btn{display:flex;align-items:center;gap:6px;background:rgba(255,68,68,0.08);border:1px solid rgba(255,68,68,0.25);border-radius:2px;color:#ff7766;padding:4px 10px;cursor:pointer;font-family:'Share Tech Mono',monospace;font-size:0.57rem;font-weight:700;letter-spacing:1.2px;transition:all 0.2s;}
    .ac-head-btn:hover{background:rgba(255,68,68,0.16);border-color:rgba(255,68,68,0.45);color:#fff;box-shadow:0 0 10px rgba(255,68,68,0.2);}

    /* Image */
    .ac-img-wrap{position:relative;height:210px;margin:1rem 1.4rem;border-radius:3px;overflow:hidden;cursor:pointer;}
    .ac-img{width:100%;height:100%;object-fit:cover;transition:transform 0.4s ease;}
    .ac:hover .ac-img{transform:scale(1.06);}
    .ac-img-overlay{position:absolute;inset:0;background:linear-gradient(to bottom,transparent 40%,rgba(6,6,10,0.85) 100%);display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity 0.3s;}
    .ac:hover .ac-img-overlay{opacity:1;}
    .ac-view-badge{display:flex;align-items:center;gap:6px;background:rgba(6,6,10,0.9);border:1px solid rgba(255,68,68,0.4);border-radius:2px;padding:6px 14px;font-family:'Share Tech Mono',monospace;font-size:0.6rem;letter-spacing:2px;color:#ff6644;box-shadow:0 0 12px rgba(255,68,68,0.2);}

    /* Body */
    .ac-body{padding:0 1.4rem 1.4rem;}
    .ac-tags{display:flex;flex-wrap:wrap;gap:0.4rem;margin-bottom:0.8rem;}
    .ac-tag{font-family:'Share Tech Mono',monospace;font-size:0.58rem;color:#ff6644;background:rgba(255,68,68,0.08);border:1px solid rgba(255,68,68,0.18);border-radius:2px;padding:2px 8px;letter-spacing:0.5px;transition:background 0.2s,border-color 0.2s;}
    .ac-tag:hover{background:rgba(255,68,68,0.15);border-color:rgba(255,68,68,0.4);}
    .ac-title{font-family:'Rajdhani',sans-serif;font-size:1.15rem;font-weight:700;color:#fff;margin-bottom:0.8rem;line-height:1.3;letter-spacing:0.3px;}
    .ac-meta{display:flex;flex-direction:column;gap:0.4rem;margin-bottom:0.9rem;}
    .ac-meta-item{display:flex;align-items:center;gap:6px;font-size:0.82rem;color:#7a8a9a;}
    .ac-desc{font-size:0.88rem;color:#7a8a9a;line-height:1.68;margin-bottom:1.1rem;}

    /* Skeleton system */
    .sk{display:block;border-radius:3px;background:linear-gradient(90deg,rgba(255,68,68,0.08) 25%,rgba(255,68,68,0.18) 50%,rgba(255,68,68,0.08) 75%);background-size:220% 100%;animation:shimmer 1.2s ease-in-out infinite;}
    @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-20% 0}}
    .sk-pill{width:90px;height:20px;}
    .sk-year{width:56px;height:20px;}
    .sk-btn{width:76px;height:20px;}
    .sk-img{width:100%;height:100%;}
    .sk-tag{width:60px;height:16px;}
    .sk-title{width:72%;height:22px;margin-bottom:0.85rem;}
    .sk-meta{width:54%;height:14px;margin-bottom:0.5rem;}
    .sk-meta-short{width:44%;}
    .sk-desc{width:100%;height:13px;margin-bottom:0.42rem;}
    .sk-desc-short{width:78%;}
    .sk-stat{width:62px;height:28px;margin:0 auto;}

    /* ── Stats ── */
    .stats-card{padding:2.5rem;width:100%;transition:border-color 0.3s;}
    .stats-card:hover{border-color:rgba(255,68,68,0.22);}
    .stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:1.5rem;margin-top:0.5rem;}
    .stat-cell{text-align:center;padding:1.5rem 1rem;background:rgba(255,68,68,0.04);border:1px solid rgba(255,68,68,0.1);border-radius:4px;position:relative;overflow:hidden;transition:border-color 0.3s,background 0.3s;}
    .stat-cell:hover{background:rgba(255,68,68,0.09);border-color:rgba(255,68,68,0.3);}
    .stat-ring{position:absolute;top:-20px;right:-20px;width:60px;height:60px;border-radius:50%;border:1px solid rgba(255,68,68,0.1);pointer-events:none;}
    .stat-num{font-family:'Orbitron',sans-serif;font-size:2rem;font-weight:900;color:#ff3333;text-shadow:0 0 12px rgba(255,51,51,0.5);margin-bottom:0.4rem;}
    .stat-lbl{font-family:'Share Tech Mono',monospace;font-size:0.58rem;color:#7a8a9a;letter-spacing:1px;text-transform:uppercase;line-height:1.4;}
    .stat-line{position:absolute;bottom:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,#ff4444,transparent);opacity:0;transition:opacity 0.3s;animation:stat-ln 3s ease-in-out infinite;}
    @keyframes stat-ln{0%,100%{opacity:0}50%{opacity:0.7}}

    /* ── Upcoming ── */
    .upcoming-card{padding:3.5rem;width:100%;text-align:center;transition:border-color 0.3s,box-shadow 0.3s;}
    .upcoming-card:hover{border-color:rgba(255,68,68,0.28);box-shadow:0 0 28px rgba(255,68,68,0.08);}
    .uc-sweep{position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse at 50% 50%,rgba(255,34,34,0.06),transparent 70%);animation:cta-sw 6s ease-in-out infinite alternate;}
    @keyframes cta-sw{from{transform:scaleX(0.85)}to{transform:scaleX(1.1)}}
    .uc-alert{display:inline-flex;align-items:center;gap:8px;font-family:'Share Tech Mono',monospace;font-size:0.6rem;color:#ff6644;letter-spacing:2px;margin-bottom:1.2rem;}
    .uca-dot{width:6px;height:6px;border-radius:50%;background:#ff4444;box-shadow:0 0 6px #ff4444;animation:blink 1s infinite;}
    .uc-title{font-family:'Orbitron',sans-serif;font-size:clamp(1.4rem,3vw,2rem);font-weight:900;color:#fff;letter-spacing:3px;margin-bottom:1rem;}
    .uc-desc{font-size:1rem;color:#7a8a9a;line-height:1.7;margin-bottom:2rem;max-width:560px;margin-left:auto;margin-right:auto;}
    .uc-btns{display:flex;gap:1.2rem;justify-content:center;flex-wrap:wrap;}

    /* Buttons */
    .btn-primary{position:relative;overflow:hidden;background:#cc1111;border:none;border-radius:3px;cursor:pointer;padding:0;font-family:'Orbitron',sans-serif;box-shadow:0 0 16px rgba(255,17,17,0.3);animation:btn-pulse 2.5s ease-in-out infinite;transition:transform 0.25s,box-shadow 0.25s;}
    @keyframes btn-pulse{0%,100%{box-shadow:0 0 14px rgba(255,34,34,0.3)}50%{box-shadow:0 0 28px rgba(255,34,34,0.6)}}
    .btn-primary:hover{transform:translateY(-2px) scale(1.03);box-shadow:0 0 32px rgba(255,34,34,0.55);}
    .btn-shine{position:absolute;top:0;left:-80%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent);transition:left 0.5s ease;}
    .btn-primary:hover .btn-shine{left:130%;}
    .btn-inner{position:relative;z-index:1;display:flex;align-items:center;gap:8px;padding:0.9rem 2rem;font-size:0.7rem;font-weight:700;color:#fff;letter-spacing:2px;}
    .btn-secondary{background:transparent;color:#ff4444;border:1px solid rgba(255,68,68,0.4);border-radius:3px;padding:0.9rem 2rem;cursor:pointer;font-family:'Orbitron',sans-serif;font-size:0.7rem;font-weight:600;letter-spacing:2px;display:flex;align-items:center;gap:8px;transition:all 0.25s;}
    .btn-secondary:hover{background:rgba(255,68,68,0.1);border-color:#ff4444;box-shadow:0 0 18px rgba(255,68,68,0.2);transform:translateY(-2px);}

    /* ── Empty ── */
    .empty-card{padding:4rem 3rem;max-width:580px;margin:0 auto;text-align:center;}
    .empty-tag{font-family:'Share Tech Mono',monospace;font-size:0.6rem;color:#555;letter-spacing:1.5px;margin-bottom:0.6rem;}
    .empty-title{font-family:'Orbitron',sans-serif;font-size:1rem;font-weight:700;color:#fff;letter-spacing:2px;margin-bottom:0.8rem;}
    .empty-desc{font-size:0.9rem;color:#7a8a9a;line-height:1.65;}
    .debug-fetch-hint{margin-top:1.25rem;font-family:'Share Tech Mono',monospace;font-size:0.58rem;color:#5a6670;line-height:1.55;max-width:520px;margin-left:auto;margin-right:auto;}

    /* ── Image Preview ── */
    .preview-overlay{position:fixed;inset:0;z-index:20000;background:rgba(2,2,6,0.92);backdrop-filter:blur(12px);display:flex;align-items:center;justify-content:center;padding-top:86px;animation:fade-in 0.25s ease;}
    @keyframes fade-in{from{opacity:0}to{opacity:1}}
    .preview-panel{position:relative;background:rgba(8,8,16,0.98);border:1px solid rgba(255,68,68,0.2);border-radius:5px;max-width:90vw;max-height:92vh;overflow:hidden;box-shadow:0 0 48px rgba(255,34,34,0.15);}
    .preview-top-line{position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,68,68,0.7),transparent);z-index:2;}
    .preview-controls{position:absolute;top:0.8rem;right:0.8rem;display:flex;flex-direction:column;gap:0.4rem;z-index:3;}
    .prev-ctrl{width:36px;height:36px;background:rgba(6,6,12,0.92);border:1px solid rgba(255,68,68,0.2);border-radius:3px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#8a97a8;transition:all 0.2s;}
    .prev-ctrl:hover{background:rgba(255,68,68,0.12);border-color:rgba(255,68,68,0.5);color:#ff4444;box-shadow:0 0 10px rgba(255,68,68,0.2);}
    .prev-close:hover{color:#ff2222;border-color:#ff2222;box-shadow:0 0 12px rgba(255,34,34,0.3);}
    .preview-img-area{overflow:auto;max-height:88vh;display:flex;align-items:center;justify-content:center;padding:1rem;}
    .preview-img{display:block;max-width:100%;height:auto;transform-origin:center;}
    .preview-zoom-tag{position:absolute;bottom:0.6rem;left:1rem;font-family:'Share Tech Mono',monospace;font-size:0.58rem;color:#555;letter-spacing:1px;}

    /* ── Loading / Error ── */
    .state-wrap{position:relative;z-index:3;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:2rem;text-align:center;}
    .loading-rig{position:relative;width:90px;height:90px;margin:0 auto 2.5rem;}
    .lr-ring{position:absolute;border-radius:50%;border-style:solid;border-color:transparent;}
    .lr-ring-1{inset:0;border-width:2px;border-top-color:#ff4444;animation:spin 1.2s linear infinite;box-shadow:0 0 12px rgba(255,68,68,0.4);}
    .lr-ring-2{inset:10px;border-width:2px;border-right-color:rgba(255,68,68,0.6);animation:spin 2s linear infinite reverse;}
    .lr-ring-3{inset:20px;border-width:1px;border-bottom-color:rgba(255,68,68,0.35);animation:spin 3s linear infinite;}
    @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
    .lr-core{position:absolute;inset:36px;border-radius:50%;background:#ff4444;box-shadow:0 0 14px #ff4444;animation:blink 1.2s ease-in-out infinite;}
    .load-tag{font-family:'Share Tech Mono',monospace;font-size:0.62rem;color:#555;letter-spacing:2px;margin-bottom:0.8rem;}
    .load-title{font-family:'Orbitron',sans-serif;font-size:1.4rem;font-weight:700;color:#fff;letter-spacing:4px;margin-bottom:0.6rem;}
    .load-sub{font-size:0.9rem;color:#7a8a9a;}
    .err-icon{margin:0 auto 1.5rem;animation:warn-pulse 2s ease-in-out infinite;}
    @keyframes warn-pulse{0%,100%{filter:drop-shadow(0 0 8px rgba(255,68,68,0.4))}50%{filter:drop-shadow(0 0 18px rgba(255,68,68,0.7))}}
    .err-tag{font-family:'Share Tech Mono',monospace;font-size:0.62rem;color:#555;letter-spacing:2px;margin-bottom:0.8rem;}
    .err-title{font-family:'Orbitron',sans-serif;font-size:1.4rem;font-weight:700;color:#fff;letter-spacing:4px;margin-bottom:0.8rem;}
    .err-msg{font-size:0.95rem;color:#7a8a9a;margin-bottom:2rem;max-width:440px;line-height:1.6;}
    .retry-btn{position:relative;overflow:hidden;background:#cc1111;border:none;border-radius:3px;cursor:pointer;padding:0;font-family:'Orbitron',sans-serif;animation:btn-pulse 2.5s ease-in-out infinite;}
    .retry-shine{position:absolute;top:0;left:-80%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent);transition:left 0.5s;}
    .retry-btn:hover .retry-shine{left:130%;}
    .retry-inner{position:relative;z-index:1;display:flex;align-items:center;gap:8px;padding:0.9rem 2rem;font-size:0.7rem;font-weight:700;color:#fff;letter-spacing:2px;}
    .retry-btn:hover{transform:translateY(-2px);box-shadow:0 0 28px rgba(255,34,34,0.5);}

    /* ── Responsive ── */
    @media(max-width:900px){.stats-grid{grid-template-columns:repeat(2,1fr);}}
    @media(max-width:768px){
      .hero{padding:5rem 0 3rem;}
      .acts-grid{grid-template-columns:1fr;}
      .stats-grid{grid-template-columns:repeat(2,1fr);}
      .uc-btns{flex-direction:column;align-items:center;}
      .btn-primary,.btn-secondary{width:100%;justify-content:center;}
      .upcoming-card,.stats-card{padding:2rem 1.5rem;}
      .preview-panel{max-width:96vw;}
    }
    @media(max-width:480px){
      .stats-grid{grid-template-columns:1fr 1fr;}
    }
  `}</style>
);

export default Activity;